import React, { useState } from "react";
import { FormikProps } from "formik";
import ATMLoadingButton from "src/components/UI/atoms/ATMLoadingButton/ATMLoadingButton";
import { QuestionBankFormValues } from "src/models/QuestionBank.model";
import ATMHTMLEditor from "src/components/UI/atoms/formFields/ATMHTMLEditor/ATMHTMLEditor";
import ATMCheckbox from "src/components/UI/atoms/formFields/ATMCheckbox/ATMCheckbox";
import ATMSelect from "src/components/UI/atoms/formFields/ATMSelect/ATMSelect";
import ATMTextField from "src/components/UI/atoms/formFields/ATMTextField/ATMTextField";
import { handleValidNumber } from "src/utils/validations/numberInput";
import { useGetAllTagsQuery } from "src/services/TagServices";
import AddTagFormDialogWrapper from "../List/AddTagDialog/AddTagFormDialogWrapper";
import ATMTagAutoComplete from "src/components/UI/atoms/formFields/ATMTagAutoComplete/ATMTagAutoComplete";

type Props = {
  formikProps: FormikProps<QuestionBankFormValues>;
  onClose: () => void;
  editorValue?: string;
  formType: "Add" | "Edit";
  setSaveNextChecked?: any;
  saveNextChecked?: any;
};

const ComplexityOptions = [
  {
    label: "SUPER_EASY",
    value: "SUPER_EASY",
  },
  {
    label: "EASY",
    value: "EASY",
  },
  {
    label: "MEDIUM",
    value: "MEDIUM",
  },
  {
    label: "HARD",
    value: "HARD",
  },
];

const QuestionBankForm = ({
  formikProps,
  onClose,
  editorValue,
  formType,
  setSaveNextChecked,
  saveNextChecked,
}: Props) => {
  const { values, setFieldValue, isSubmitting, handleBlur } = formikProps;

  const [isAddTag, setIsAddTag] = useState(false);

  //Tags Data
  const { data: AllTagsData } = useGetAllTagsQuery("");
  const tagsOptions = AllTagsData?.data?.map((el: any) => ({
    title: el?.tagName,
  }));
  return (
    <div className="flex flex-col gap-6 p-4">
      <div className="flex items-center justify-between">
        <div className="text-xl font-medium">
          {formType === "Add" ? "Add Question" : "Update Question"}
        </div>
        <button type="button" className="hover:bg-hover" onClick={onClose}>
          Cancel
        </button>
      </div>
      <div className="flex flex-col gap-4">
        {/* Field Label */}
        <div className="">
          <ATMTextField
            required
            name="title"
            value={values?.title}
            onChange={(e) => setFieldValue("title", e.target.value)}
            label="Title"
            placeholder="Enter Title"
            onBlur={handleBlur}
          />
        </div>
        {/* Questions */}
        <div className="z-0">
          <ATMHTMLEditor
            key={editorValue}
            label="Question"
            required
            value={values?.question}
            placeholder="Write description here...."
            onChange={(newValue: any) => {
              setFieldValue("question", newValue);
            }}
          />
        </div>
        <div className="grid grid-cols-3 gap-4">
          {/* Complexity */}
          <div>
            <ATMSelect
              name="complexity"
              required
              value={values?.complexity}
              onChange={(newValue) => setFieldValue("complexity", newValue)}
              onBlur={handleBlur}
              label="Complexity"
              placeholder="Select Complexity"
              options={ComplexityOptions}
            />
          </div>
          {/* Order */}
          <div>
            <ATMTextField
              name="order"
              required
              value={values?.order}
              onChange={(e) =>
                handleValidNumber(e) && setFieldValue("order", e.target.value)
              }
              onBlur={handleBlur}
              label="Order"
              placeholder="Order"
            />
          </div>
        </div>
        <div>
          <ATMTagAutoComplete
            value={values?.tagId}
            onChange={(value: any) => {
              setFieldValue("tagId", value);
            }}
            label="Tags"
            required
            option={tagsOptions || []}
            multiple
          />
        </div>

        {/* solution */}
        <div className="z-0">
          <ATMHTMLEditor
            key={editorValue}
            label="Solution"
            value={values?.solution}
            placeholder="Write description here...."
            onChange={(newValue: any) => {
              setFieldValue("solution", newValue);
            }}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* explanationVideo */}
          <div className="z-0">
            <ATMTextField
              name="solutionVideoURL"
              value={values?.solutionVideoURL}
              onChange={(e) =>
                setFieldValue("solutionVideoURL", e.target.value)
              }
              label="Solution Video URL"
              placeholder="Solution Video URL"
              onBlur={handleBlur}
            />
          </div>
          {/* ExplanationVideo */}
          <div className="z-0">
            <ATMTextField
              name="explanationVideoURL"
              value={values?.explanationVideoURL}
              onChange={(e) =>
                setFieldValue("explanationVideoURL", e.target.value)
              }
              label="Explanation Video URL"
              placeholder="Explanation Video URL"
              onBlur={handleBlur}
            />
          </div>
          {/* HintVideo */}
          <div className="z-0">
            <ATMTextField
              name="hintVideoUrl"
              value={values?.hintVideoUrl}
              onChange={(e) => setFieldValue("hintVideoUrl", e.target.value)}
              label="Hint Video URL"
              placeholder="Hint Video URL"
              onBlur={handleBlur}
            />
          </div>
        </div>
      </div>
      {formType === "Add" && (
        <div>
          <ATMCheckbox
            label="Save and Next"
            checked={saveNextChecked}
            onChange={(checked) => {
              setSaveNextChecked(checked);
            }}
          />
        </div>
      )}
      <div>
        <ATMLoadingButton isLoading={isSubmitting} type="submit">
          Save
        </ATMLoadingButton>
      </div>
      {isAddTag ? (
        <AddTagFormDialogWrapper
          onClose={() => {
            setIsAddTag(false);
          }}
        />
      ) : null}
    </div>
  );
};

export default QuestionBankForm;
